import { DateTime } from 'luxon';
import { assign, setup } from 'xstate';

/** Luxon weekdays Mon–Sun matching `standingOrderDayConfigs` order. */
const STANDING_ORDER_WEEKDAY = [1, 2, 3, 4, 5, 6, 7] as const;

export type StandingOrderSummaryInputs = {
  appointmentDate?: unknown;
  appointmentDateTime?: unknown;
  pickupDateTime?: unknown;
  roundTripReturnDateTime?: unknown;
  returnTypeValues?: (string | undefined)[];
  roundTripWillCall?: boolean | undefined;
  standingOrderStartDate?: unknown;
  standingOrderEndDate?: unknown;
  enabledValues?: boolean[] | undefined;
  /** Mon–Sun pickup clock times aligned with `enabledValues`. */
  pickupTimesPerDay?: unknown[] | undefined;
};

function coerceDateTime(value: unknown): DateTime | null {
  return value instanceof DateTime && value.isValid ? value : null;
}

/*
* First calendar day in [rangeStart, rangeEnd] (inclusive, date-only) with the given Luxon weekday, or null.
* We need this to ensure that the day they selected can actually land within the date ranges they selected.
* @param rangeStart - The start of the range
* @param rangeEnd - The end of the range
* @param weekday - The weekday to find
* @returns The first calendar day in [rangeStart, rangeEnd] (inclusive, date-only) with the given Luxon weekday, or null
*/
function firstCalendarDayWithWeekdayInRange(
  rangeStart: DateTime,
  rangeEnd: DateTime,
  weekday: number,
): DateTime | null {
  const start = rangeStart.startOf('day');
  const end = rangeEnd.startOf('day');
  if (!start.isValid || !end.isValid || end < start) return null;

  const delta = (weekday - start.weekday + 7) % 7;
  const candidate = start.plus({ days: delta });
  if (candidate > end) return null;
  return candidate;
}

/**
 * Comma-separated ISO datetimes for each enabled weekday
 * combined with that day's pickup clock time (Mon→Sun order).
 * @param inputs - The inputs required for calculating the CSV string
 * @returns The comma-separated ISO datetimes for each enabled weekday
 */
export function standingOrderPickupDateTimesCsv(
  inputs: StandingOrderSummaryInputs,
): string {
  const rangeStart = coerceDateTime(inputs.standingOrderStartDate);
  const rangeEnd = coerceDateTime(inputs.standingOrderEndDate);
  const enabled = inputs.enabledValues ?? [];
  const pickups = inputs.pickupTimesPerDay ?? [];

  if (!rangeStart || !rangeEnd) return '';

  const isoParts: string[] = [];

  for (let i = 0; i < STANDING_ORDER_WEEKDAY.length; i++) {
    if (!enabled[i]) continue;
    const pickup = coerceDateTime(pickups[i]);
    if (!pickup) continue;

    const day = firstCalendarDayWithWeekdayInRange(
      rangeStart,
      rangeEnd,
      STANDING_ORDER_WEEKDAY[i],
    );
    if (!day) continue;

    const combined = day.set({
      hour: pickup.hour,
      minute: pickup.minute,
      second: pickup.second,
      millisecond: pickup.millisecond,
    });

    const iso = combined.toISO();
    if (iso) isoParts.push(iso);
  }

  return isoParts.join(', ');
}

/** One boolean per trip index (`true` = standing order enabled). */
export type StandingOrderContext = {
  values: boolean[];
  focusedIndex: number | null;
  /** Whether the standing-order summary strip should show for each trip leg. */
  shouldDisplaySummary: boolean[];
  /** Comma-separated ISO datetimes for each enabled weekday */
  pickupDateTimesCsv: string[];
};

/*
 * Determines whether or not enough data is present to display the standing order summary.
 * @param inputs - The inputs required for determining if the standing order summary should be displayed
 * @returns Whether the standing order summary should be displayed
 */
export function shouldDisplayStandingOrderSummary(
  inputs: StandingOrderSummaryInputs,
): boolean {
  const {
    appointmentDate,
    appointmentDateTime,
    pickupDateTime,
    roundTripReturnDateTime,
    returnTypeValues,
    roundTripWillCall,
    standingOrderStartDate,
    standingOrderEndDate,
    enabledValues,
  } = inputs;

  const hasAppointment = !!(appointmentDate || appointmentDateTime);
  const hasPickup = !!(pickupDateTime || roundTripReturnDateTime);
  const hasReturn =
    (returnTypeValues?.some((value) => value) ?? false) ||
    !!roundTripWillCall;
  const isDaySelected = enabledValues?.some((value) => value) ?? false;

  return (
    hasAppointment &&
    hasPickup &&
    hasReturn &&
    !!standingOrderStartDate &&
    !!standingOrderEndDate &&
    isDaySelected
  );
}

export type StandingOrderEvent =
  | { type: 'CHANGE'; index: number; value: boolean }
  | { type: 'RESIZE'; count: number }
  | { type: 'RESET' }
  | { type: 'FOCUS'; index: number }
  | { type: 'BLUR'; index: number }
  | {
      type: 'SUMMARY_INPUTS';
      index: number;
      inputs: StandingOrderSummaryInputs;
    };

function resizeValues(prev: boolean[], count: number): boolean[] {
  const c = Math.max(1, count);
  const next = prev.slice(0, c);
  while (next.length < c) next.push(false);
  return next;
}

function resizeCsvStrings(prev: string[], count: number): string[] {
  const c = Math.max(1, count);
  const next = prev.slice(0, c);
  while (next.length < c) next.push('');
  return next;
}

export const standingOrderMachine = setup({
  types: {
    context: {} as StandingOrderContext,
    events: {} as StandingOrderEvent,
  },
}).createMachine({
  id: 'standingOrder',
  context: {
    values: [false],
    focusedIndex: null,
    shouldDisplaySummary: [false],
    pickupDateTimesCsv: [''],
  },
  initial: 'idle',
  states: {
    idle: {
      on: {
        FOCUS: {
          target: 'focused',
          actions: assign({
            focusedIndex: ({ event }) =>
              event.type === 'FOCUS' ? event.index : null,
          }),
        },
        CHANGE: {
          actions: assign({
            values: ({ context, event }) => {
              if (event.type !== 'CHANGE') return context.values;
              const next = [...context.values];
              while (next.length <= event.index) next.push(false);
              next[event.index] = event.value;
              return next;
            },
          }),
        },
        RESIZE: {
          actions: assign({
            values: ({ context, event }) =>
              event.type === 'RESIZE'
                ? resizeValues(context.values, event.count)
                : context.values,
            shouldDisplaySummary: ({ context, event }) =>
              event.type === 'RESIZE'
                ? resizeValues(context.shouldDisplaySummary, event.count)
                : context.shouldDisplaySummary,
            pickupDateTimesCsv: ({ context, event }) =>
              event.type === 'RESIZE'
                ? resizeCsvStrings(context.pickupDateTimesCsv, event.count)
                : context.pickupDateTimesCsv,
          }),
        },
      },
    },
    focused: {
      on: {
        CHANGE: {
          actions: assign({
            values: ({ context, event }) => {
              if (event.type !== 'CHANGE') return context.values;
              const next = [...context.values];
              while (next.length <= event.index) next.push(false);
              next[event.index] = event.value;
              return next;
            },
          }),
        },
        RESIZE: {
          actions: assign({
            values: ({ context, event }) =>
              event.type === 'RESIZE'
                ? resizeValues(context.values, event.count)
                : context.values,
            shouldDisplaySummary: ({ context, event }) =>
              event.type === 'RESIZE'
                ? resizeValues(context.shouldDisplaySummary, event.count)
                : context.shouldDisplaySummary,
            pickupDateTimesCsv: ({ context, event }) =>
              event.type === 'RESIZE'
                ? resizeCsvStrings(context.pickupDateTimesCsv, event.count)
                : context.pickupDateTimesCsv,
          }),
        },
        BLUR: {
          target: 'idle',
          actions: assign({
            focusedIndex: ({ context, event }) =>
              event.type === 'BLUR' && context.focusedIndex === event.index
                ? null
                : context.focusedIndex,
          }),
        },
        FOCUS: {
          actions: assign({
            focusedIndex: ({ event }) =>
              event.type === 'FOCUS' ? event.index : null,
          }),
        },
      },
    },
  },
  on: {
    RESET: {
      target: '.idle',
      actions: assign({
        values: [false],
        focusedIndex: null,
        shouldDisplaySummary: [false],
        pickupDateTimesCsv: [''],
      }),
    },
    SUMMARY_INPUTS: {
      actions: assign({
        shouldDisplaySummary: ({ context, event }) => {
          if (event.type !== 'SUMMARY_INPUTS') {
            return context.shouldDisplaySummary;
          }
          const next = [...context.shouldDisplaySummary];
          while (next.length <= event.index) next.push(false);
          next[event.index] = shouldDisplayStandingOrderSummary(event.inputs);
          return next;
        },
        pickupDateTimesCsv: ({ context, event }) => {
          if (event.type !== 'SUMMARY_INPUTS') {
            return context.pickupDateTimesCsv;
          }
          const next = [...context.pickupDateTimesCsv];
          while (next.length <= event.index) next.push('');
          next[event.index] = standingOrderPickupDateTimesCsv(event.inputs);
          return next;
        },
      }),
    },
  },
});

